import { ChevronLeftIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { AudienceId } from "@sp-crm/core";
import { Content, ContentHeader } from "components/layout";
import { DeleteButton } from "components/ui/action-button";
import { InlineBanner } from "components/ui/inline-banner";
import { defaultLinkStyle } from "components/ui/link";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import {
    GetAudienceQuery,
    TenantCredentialStatus,
    TenantCredentialType,
    useDeleteAudienceMutation,
    useGetAudienceQuery,
    useGetEntitiesQuery,
    useGetMailchimpListsQuery,
    useGetTenantCredentialsQuery,
    useTriggerAudienceSyncMutation,
    useUpdateAudienceMutation,
} from "generated/graphql";
import React, { useState } from "react";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";
import { navigate } from "store/actions";
import { useRegionId } from "store/selectors/hooks";
import { stableQueryOptions } from "util/requests";
import { AudienceMembers } from "./audience-members";
import { AudienceQueryInput } from "./audience-query-input";
import { MailchimpListSelectionDialog } from "./mailchimp-list-selection-dialog";

export interface AudienceShowProps {
    audienceId: AudienceId;
    subtab: string;
}

export const AudienceShow: React.FC<AudienceShowProps> = props => {
    const { audienceId, subtab } = props;
    const regionId = useRegionId();

    const getAudienceQuery = useGetAudienceQuery({
        input: {
            id: audienceId,
            regionId,
        },
    });
    const getEntitiesQuery = useGetEntitiesQuery({}, { ...stableQueryOptions() });
    const getMailchimpListsQuery = useGetMailchimpListsQuery({
        input: {
            regionId,
        },
    });
    const getTenantCredentialsQuery = useGetTenantCredentialsQuery();
    const updateAudienceMutation = useUpdateAudienceMutation();
    const deleteAudienceMutation = useDeleteAudienceMutation();
    const triggerSyncMutation = useTriggerAudienceSyncMutation();
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const initialMode = urlParams.get("initialMode") === "edit" ? "edit" : "view";

    const [mode, setMode] = useState<"view" | "edit">(initialMode);
    const [showListDialog, setShowListDialog] = useState(false);

    const [audience, setAudience] = useState<GetAudienceQuery["getAudience"] | null>(
        null,
    );

    React.useEffect(() => {
        if (getAudienceQuery.data?.getAudience) {
            setAudience(getAudienceQuery.data.getAudience);
        }
    }, [getAudienceQuery.data?.getAudience]);

    const handleAudienceChange = async (value: GetAudienceQuery["getAudience"]) => {
        setMode("edit");
        setAudience(value);
    };

    const handleEdit = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setMode("edit");
    };

    const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setMode("view");
        setAudience(getAudienceQuery.data?.getAudience || null);
    };

    const onSaveCriteria = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        await updateAudienceMutation.mutateAsync({
            input: {
                id: audienceId,
                query: audience?.query,
                name: audience?.name || "",
                regionId,
            },
        });
        getAudienceQuery.refetch();
        setMode("view");
    };

    const onDelete = async () => {
        await deleteAudienceMutation.mutateAsync({
            input: {
                id: audienceId,
                regionId,
            },
        });
        navigate("/settings/integrations/mailchimp");
    };

    const onSyncToMailchimp = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        const lists = getMailchimpListsQuery.data?.getMailchimpLists || [];

        if (lists.length === 0) {
            // Handle no lists case - could show an error message
            alert("No Mailchimp lists found. Please create a list in Mailchimp first.");
            return;
        } else if (lists.length === 1) {
            // Only one list - sync directly
            await triggerSyncMutation.mutateAsync({
                input: {
                    audienceId: audienceId,
                    listId: lists[0].id,
                    regionId,
                },
            });
            // Refetch audience data to show the updated tag
            getAudienceQuery.refetch();
        } else {
            // Multiple lists - show selection dialog
            setShowListDialog(true);
        }
    };

    const onSelectList = async (listId: string) => {
        setShowListDialog(false);
        await triggerSyncMutation.mutateAsync({
            input: {
                audienceId: audienceId,
                listId: listId,
                regionId,
            },
        });
        // Refetch audience data to show the updated tag
        getAudienceQuery.refetch();
    };

    const entities = getEntitiesQuery?.data?.getEntities || [];

    const mailchimpCredential = getTenantCredentialsQuery.data?.getTenantCredentials.find(
        credential => credential.type === TenantCredentialType.Mailchimp,
    );
    const isMailchimpConnected =
        mailchimpCredential?.status === TenantCredentialStatus.Active;
    const hasMailchimpTag = !!audience?.mailchimpTag;

    if (!audience) {
        return null;
    }

    return (
        <>
            <div className="mb-2">
                <Link to="/settings/integrations/mailchimp" className={defaultLinkStyle}>
                    <div className="flex space-x-2 items-center">
                        <ChevronLeftIcon className="h-4 w-4" />
                        <span>Back to Mailchimp</span>
                    </div>
                </Link>
            </div>
            <div className="space-y-4">
                <Content>
                    <ContentHeader>
                        <div className="flex items-center justify-between">
                            <span>Contact List</span>
                            <div className="flex items-center space-x-2">
                                {mode === "edit" ? (
                                    <SecondaryButton
                                        className="flex items-center space-x-2"
                                        onClick={handleClose}>
                                        <ChevronUpIcon className="w-4 h-4" />
                                        <div>Close editor</div>
                                    </SecondaryButton>
                                ) : (
                                    <SecondaryButton onClick={handleEdit}>
                                        Edit definition
                                    </SecondaryButton>
                                )}
                                <DeleteButton
                                    onClick={onDelete}
                                    backgroundColor="bg-white"
                                    confirm={{
                                        title: "Delete Contact List?",
                                        message: `Delete "${audience?.name}" contact list? This action cannot be undone.`,
                                    }}
                                />
                            </div>
                        </div>
                    </ContentHeader>
                    <AudienceQueryInput
                        mode={mode}
                        audience={audience}
                        onChange={handleAudienceChange}
                    />
                    {mode === "edit" ? (
                        <div className="flex items-center space-x-4 mt-8">
                            <PrimaryButton onClick={onSaveCriteria}>Save</PrimaryButton>
                            <SecondaryButton onClick={handleClose}>
                                Cancel
                            </SecondaryButton>
                            {updateAudienceMutation.isLoading ? <Spinner /> : null}
                        </div>
                    ) : (
                        <>
                            {isMailchimpConnected && (
                                <div className="flex items-center space-x-4 mt-8">
                                    <PrimaryButton onClick={onSyncToMailchimp}>
                                        Sync to Mailchimp
                                    </PrimaryButton>
                                    {triggerSyncMutation.isLoading ? <Spinner /> : null}
                                </div>
                            )}
                            {!isMailchimpConnected && hasMailchimpTag && (
                                <div className="mt-8">
                                    <InlineBanner type="warning">
                                        Your Mailchimp account has been disconnected.
                                        Please connect it again to sync with Mailchimp.
                                    </InlineBanner>
                                </div>
                            )}
                        </>
                    )}
                </Content>
                {getAudienceQuery?.data?.getAudience?.query?.queries?.length > 0 ? (
                    <AudienceMembers
                        key={getAudienceQuery.data.getAudience.updatedAt.toString()}
                        audience={getAudienceQuery.data.getAudience}
                        entities={entities}
                        subtab={subtab}
                    />
                ) : null}
            </div>

            <MailchimpListSelectionDialog
                isOpen={showListDialog}
                lists={getMailchimpListsQuery.data?.getMailchimpLists || []}
                isLoading={triggerSyncMutation.isLoading}
                onDismiss={() => setShowListDialog(false)}
                onSelectList={onSelectList}
            />
        </>
    );
};
